import App from 'next/app'
import Script from 'next/script'
import React from 'react'
import pclnExpt from '@pcln/experimentation'
import { ThemeProvider } from 'pcln-design-system'
import { tag } from '@pcln/measure'
import { brandRefreshTheme } from '@pcln/brand'
import BootstrapDataContext from '../context/BootstrapDataContext'
import ExperimentContext from '../context/ExperimentContext'
import { SetiRedesignProvider } from '../components/providers/RedesignProvider'
import {
  BaseSetiResponseEvent,
  BootstrapData,
  Referral,
  Webstats
} from '../types'
import '../styles/main.scss'
import getTMS from '../utils/tms'

const PRODUCT_CODES = {
  flights: 1,
  hotels: 5,
  packages: 17,
  home: 16
} as { [key: string]: number }

/**
 *
 * @param {Array<Object>} fullSETI - full list of seti tests being run
 * @param {boolean} isMobile - whether a desktop or mobile browser
 * @param {string} key - product name i.e. packages, hotels, etc
 * @param {Object} referral - traffic info
 * @param {Object} webstats - client info, cguid, user agent..
 */
function initializePclnExpt({
  fullSETI = [],
  isMobile = false,
  key: productCode = '',
  referral,
  webstats
}: {
  fullSETI: BaseSetiResponseEvent[]
  isMobile: boolean
  key: string
  referral: Referral
  webstats: Webstats
}): void {
  const webAppMetadata = {
    appCode: isMobile ? 'MOBILEWEB' : 'DESKTOPWEB',
    clientIP: webstats.resolvedClientIP,
    globalPartnerCode: 'PCLN',
    partnerCode: 'pcln',
    productID: PRODUCT_CODES[productCode],
    productSubType: 'ALL',
    referralClickID: referral.clickId,
    referralID: referral.id,
    referralSourceID: referral.sourceId,
    serverTimestamp: webstats.requestTime
  }

  if (productCode !== '') {
    pclnExpt.initialize(fullSETI, webAppMetadata, {
      useBatching: true,
      timeOutForApiCalls: 8000 // override the default of 5000
    })
  }
}

export default class MyApp extends App {
  clearBatchHandler = () => {
    const {
      pageProps: {
        bootstrapData: { key = '' }
      }
    } = this.props
    if (key !== '') {
      pclnExpt.clearBatch()
    }
  }

  componentDidMount() {
    const {
      pageProps: { bootstrapData }
    } = this.props
    if (bootstrapData) {
      initializePclnExpt(bootstrapData)
      window.addEventListener('beforeunload', this.clearBatchHandler)
    }
  }

  componentWillUnmount() {
    const {
      pageProps: { bootstrapData }
    } = this.props
    if (bootstrapData) {
      window.removeEventListener('beforeunload', this.clearBatchHandler)
    }
  }

  render() {
    const { Component, pageProps, router } = this.props
    const bootstrapData = pageProps?.bootstrapData as BootstrapData | undefined

    // this will add a tag to the boomerang events with the appName and appVersion
    if (typeof window !== 'undefined' && bootstrapData) {
      tag('appName', bootstrapData.appName || 'next-landing')
      tag('appVersion', bootstrapData.appVersion)
    }
    const isHPCornerShadowColorRedesign =
      bootstrapData?.seti?.HP_CORNERS_SHADOWS_COLORS === 'VARIANT' &&
      bootstrapData?.key === 'home'
    const isHotelsCornerShadClrVariant =
      bootstrapData?.seti?.HTL_LP_CORNERS_SHADOWS_COLORS === 'VARIANT' &&
      bootstrapData?.key === 'hotels'
    const shouldUseBrandRefreshTheme =
      isHPCornerShadowColorRedesign || isHotelsCornerShadClrVariant
    return (
      <ThemeProvider
        theme={shouldUseBrandRefreshTheme ? brandRefreshTheme : ''}
      >
        <BootstrapDataContext.Provider
          value={(bootstrapData ?? {}) as BootstrapData}
        >
          <ExperimentContext.Provider value={bootstrapData?.seti ?? {}}>
            <SetiRedesignProvider>
              <Component {...pageProps} />
            </SetiRedesignProvider>
          </ExperimentContext.Provider>
        </BootstrapDataContext.Provider>
        {bootstrapData &&
          bootstrapData.featureToggles?.whiteLabel
            ?.pclnTagManagementScriptEnabled !== false && (
            <Script
              id="pclntms-script"
              strategy="afterInteractive"
              src="/svcs/mkt/tag/pclntms.js"
              onLoad={() => {
                if (window.pclntms) {
                  const { isMobile, referral, seti, webstats, mhe } =
                    bootstrapData
                  window.pclntms.push(
                    getTMS({
                      isMobile,
                      requestPath: router.pathname,
                      mhe,
                      seti,
                      webstats,
                      referral
                    })
                  )
                }
              }}
            />
          )}
      </ThemeProvider>
    )
  }
}
